import React, {
  ReactElement, useMemo, useRef, useState, useEffect,
} from 'react';
import {
  Empty, message, Select, Spin,
} from 'antd';
import type { SelectProps } from 'antd';
import debounce from '@/shared/helpers/debounce';
import { RefSelectProps } from 'antd/lib/select';
import useCustomTranslation from '@/hooks/useCustomTranslation';

export interface selectOptions {
  value: number | string;
  label: string;
  tag?: string;
  imageUrl?: string;
  disabled?: boolean;
}
export interface DebounceSelectProps<T> {
  fetchOptions: (search: any) => Promise<T[]>;
  refactFunc: (list: T[]) => SelectProps['options'];
  debounceTimeout?: number;
  initialOptions?: SelectProps['options'];
  onSelect?: (e: any) => void;
  onClear?: () => void;
  onDeselect?: (e: any) => void;
  onSearch?: (e: string) => void;
  onChange?: any;
  mode?: 'multiple' | 'tags';
  popupRender?: (menu: any) => React.ReactElement<any>;
  searchValue?: string;
  placeholder?: string;
  style?: React.CSSProperties;
  // eslint-disable-next-line no-restricted-globals
  open?: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<RefSelectProps>,
  forceReload?: boolean | number | string;
  dependencies?: unknown[];
  labelInValue?: boolean;
  maxTagCount?: SelectProps['maxTagCount'];
}

const DebounceSelect = <T, >({
  debounceTimeout = 400,
  refactFunc,
  fetchOptions,
  initialOptions,
  onSelect,
  onDeselect,
  onClear,
  onChange,
  mode,
  popupRender,
  onSearch,
  searchValue,
  placeholder,
  style,
  open,
  disabled = false,
  inputRef,
  forceReload,
  dependencies = [],
  labelInValue = false,
  ...props
}: DebounceSelectProps<T>) => {
  const { t } = useCustomTranslation();

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SelectProps['options'] | []>(initialOptions || []);
  const [notFoundContent, setNotFoundContent] = useState<ReactElement | null>(null);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: any) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      setOptions([]);
      setLoading(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        const refactorOptions = refactFunc(newOptions);
        setOptions(refactorOptions);
        setLoading(false);
      }).catch((e) => {
        setOptions([]);
        setLoading(false);
        // sin este log el error real quedaba invisible y solo se veia un toast generico
        console.error('DebounceSelect: fallo fetchOptions', e);
        message.error(t('g.error'));
      });
    };
    return debounce(loadOptions, debounceTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOptions, debounceTimeout]);

  const selectNotFoundContent = (): void => {
    if (loading) {
      setNotFoundContent(<Spin size="small" />);
    } else if (options?.length === 0) {
      setNotFoundContent(<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('g.results_not_found')} />);
    } else {
      setNotFoundContent(null);
    }
  };

  useEffect(() => { 
    selectNotFoundContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, options]);

  useEffect(() => { // important (Not coment this line)
    if (initialOptions && initialOptions.length > 0) {
      // console.log('initialOptions', initialOptions);
      setOptions(initialOptions);
    } else {
      debounceFetcher('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  useEffect(() => {
    debounceFetcher(searchValue);
  }, [forceReload, ...dependencies]);

  useEffect(() => {
    if (searchValue !== undefined) {
      debounceFetcher(searchValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return (
    <Select
      showSearch
      onDeselect={onDeselect}
      onClear={onClear}
      allowClear
      filterOption={false}
      onSearch={(val) => {
        if (onSearch) onSearch(val);
        debounceFetcher(val); 
      }}
      notFoundContent={notFoundContent}
      onSelect={onSelect}
      ref={inputRef}
      mode={mode}
      searchValue={searchValue}
      popupRender={popupRender}
      placeholder={placeholder}
      style={style}
      onChange={onChange}
      open={open}
      disabled={disabled}
      options={options}
      labelInValue={labelInValue}
      {...props}
    />
  );
};

export default DebounceSelect;
